import type { SoundKind } from '../core/types';

/** Synthesized UI sounds — no audio files. The context is created lazily on first gesture. */
let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let volume = 0.7;

export function audioContext(): AudioContext | null {
  if (ctx?.state === 'closed') ctx = null; // can't be revived; start a fresh one
  if (!ctx) {
    const AC =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return null;
    try {
      ctx = new AC();
    } catch {
      return null;
    }
    master = ctx.createGain();
    master.gain.value = volume;
    master.connect(ctx.destination);
  }
  // 'suspended', or Safari's 'interrupted' (another app took the audio session) — not just the former
  if (ctx.state !== 'running') void ctx.resume().catch(() => {});
  return ctx;
}

/**
 * Plays once the context is actually running. Sounds scheduled on a paused context are dropped
 * or bunched up, so a context that is not running is resumed first.
 */
function whenRunning(play: (c: AudioContext) => void) {
  const c = audioContext();
  if (!c) return;
  if (c.state === 'running') return play(c);
  c.resume().then(
    () => play(c),
    () => {}, // no user gesture yet: nothing can play
  );
}

export const ensureAudio = () => void audioContext();

export function setVolume(v: number) {
  volume = v / 100;
  if (master) master.gain.value = volume;
}

function tone(c: AudioContext, t: number, f: number, mult: number, g: number, dur: number) {
  const o = c.createOscillator();
  o.type = 'sine';
  o.frequency.value = f * mult;
  const e = c.createGain();
  e.gain.setValueAtTime(0.0001, t);
  e.gain.exponentialRampToValueAtTime(Math.max(0.0002, g), t + 0.01);
  e.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  o.connect(e).connect(master!);
  o.start(t);
  o.stop(t + dur + 0.05);
}

function noise(c: AudioContext, t: number, fq: number, q: number, g: number, dur: number) {
  const len = Math.max(1, Math.floor(c.sampleRate * dur));
  const buf = c.createBuffer(1, len, c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  const src = c.createBufferSource();
  src.buffer = buf;
  const bp = c.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = fq;
  bp.Q.value = q;
  const e = c.createGain();
  e.gain.setValueAtTime(g, t);
  e.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  src.connect(bp).connect(e).connect(master!);
  src.start(t);
}

/** One bell strike: a fundamental plus inharmonic partials; `len` stretches the decay. */
function bellStrike(c: AudioContext, t: number, len: number) {
  tone(c, t, 587, 1, 0.55, 1.7 * len);
  tone(c, t, 587, 2, 0.2, 1.2 * len);
  tone(c, t, 587, 2.92, 0.09, 0.8 * len);
  tone(c, t, 587, 4.2, 0.04, 0.5 * len);
}

function marimbaNote(c: AudioContext, t: number, f: number, len: number) {
  tone(c, t, f, 1, 0.8, 0.7 * len);
  tone(c, t, f, 4, 0.18, 0.4 * len);
  tone(c, t, f, 9.2, 0.05, 0.3 * len);
}

function woodKnock(c: AudioContext, t: number, len: number) {
  noise(c, t, 1150, 9 / len, 0.9, 0.16 * len); // lower Q + longer tail = a deeper, woodier knock
}

export function chime(kind: SoundKind) {
  whenRunning((c) => {
    const t = c.currentTime + 0.03;
    switch (kind) {
      case 'wood':
        [0, 0.22, 0.44].forEach((dt) => woodKnock(c, t + dt, 1));
        tone(c, t, 420, 1, 0.22, 0.3);
        break;
      case 'wood2': // two triplets of longer knocks, each group with a body resonance (~2.4 s)
        [0, 1.2].forEach((group) => {
          [0, 0.24, 0.48].forEach((dt) => woodKnock(c, t + group + dt, 1.6));
          tone(c, t + group, 420, 1, 0.22, 0.75);
        });
        break;
      case 'marimba':
        [0, 0.28].forEach((dt) => marimbaNote(c, t + dt, 523, 1));
        break;
      case 'marimba2': // a rising-and-falling phrase with longer notes (~2.8 s)
        [523, 659, 784, 1047, 784, 1047].forEach((f, i) => marimbaNote(c, t + i * 0.26, f, 1.6));
        break;
      case 'bell2': // three strikes a second apart, each ringing twice as long (~5.5 s)
        [0, 1.1, 2.2].forEach((dt) => bellStrike(c, t + dt, 2));
        break;
      default:
        [0, 0.42].forEach((dt) => bellStrike(c, t + dt, 1));
    }
  });
}

export function tick() {
  whenRunning((c) => noise(c, c.currentTime + 0.01, 3200, 6, 0.12, 0.03));
}

export function tap() {
  whenRunning((c) => noise(c, c.currentTime + 0.01, 1500, 8, 0.22, 0.05));
}

export function pips() {
  whenRunning((c) => {
    const t = c.currentTime + 0.03;
    [0, 0.2, 0.4].forEach((dt) => tone(c, t + dt, 880, 1, 0.5, 0.22));
  });
}

export function haptic(ms = 4) {
  if ('vibrate' in navigator) {
    try {
      navigator.vibrate(ms);
    } catch {
      /* not allowed without user activation */
    }
  }
}
